import React from "react";
import { createAIPlan, PlanStep } from "../api/client";

const AI_INPUT_MAX_CHARACTERS = 6000;
const AI_INPUT_WARNING_CHARACTERS = 4800;

type PlanBuilderProps = {
  onPlanCreated: (steps: PlanStep[], goal: string) => void;
  introduction?: boolean;
};

export default function PlanBuilder({ onPlanCreated, introduction = false }: PlanBuilderProps) {
  const [goal, setGoal] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  
  async function handleCreatePlan() {
    setError(null);

    if (!goal.trim()) {
      setError("Please enter a goal.");
      return;
    }

    if (goal.length > AI_INPUT_MAX_CHARACTERS) {
      setError("Please shorten this to 6,000 characters before continuing.");
      return;
    }

      setLoading(true);

    try {
  const result = await createAIPlan({
  goal: goal.trim(),
  time_available: "Not specified",
  skill_level: "beginner",
});

  onPlanCreated(result.steps || [], goal.trim());
} catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong creating the plan.");
    } finally {
      setLoading(false);
    }
  }
  return (
  <section style={{ maxWidth: 820, margin: "0 auto" }}>
    <h2 style={{ marginTop: 0 }}>
      {introduction ? "What would you like help with?" : "Project Guide"}
    </h2>
    <p style={{ opacity: 0.8, fontSize: "1rem" }}>
      {introduction
        ? "Bring a problem, task, project or dream. Sanctuary will turn it into manageable steps you can work through at your pace."
        : "Describe what you want to achieve and Sanctuary will help you break it into manageable steps."}
    </p>

    <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
      <div>
        <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
          {introduction ? "Tell Sanctuary what you want help with" : "What do you want to achieve?"}
        </label>
        <textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          maxLength={AI_INPUT_MAX_CHARACTERS}
          rows={4}
          placeholder="For example: Create a presentation, learn a new skill, solve a problem, or begin a project"
          style={{
            width: "100%",
            padding: 12,
            borderRadius: 8,
            border: "1px solid #ccc",
            fontSize: "1rem",
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
        {goal.length >= AI_INPUT_WARNING_CHARACTERS ? (
          <p
            aria-live="polite"
            style={{
              margin: "0.4rem 0 0",
              fontSize: "0.9rem",
              opacity: 0.8,
              color:
                goal.length >= AI_INPUT_MAX_CHARACTERS
                  ? "#f59e0b"
                  : "inherit",
            }}
          >
            {goal.length >= AI_INPUT_MAX_CHARACTERS
              ? "You have reached the 6,000-character limit."
              : `You have ${(
                  AI_INPUT_MAX_CHARACTERS - goal.length
                ).toLocaleString()} characters left.`}
          </p>
        ) : null}
      </div>

      <div style={{ marginTop: 4 }}>
        <button
          type="button"
          onClick={handleCreatePlan}
          disabled={loading}
          style={{
            padding: "12px 18px",
            borderRadius: 8,
            border: "1px solid #ccc",
            cursor: loading ? "default" : "pointer",
            fontSize: "1rem",
          }}
        >
         {loading ? "Creating your first project..." : introduction ? "Create my first project" : "Break this into steps"}
        </button>
      </div>
    </div>

    {error && (
      <p style={{ marginTop: 16, color: "#b00020" }}>
        {error}
      </p>
    )}
  </section>
);
}
